import type {
  CreatePlanOfStudyPayload,
  PlanOfStudyDetail,
  PlanOfStudyGradeSetting,
  PlanOfStudyRequirement,
} from '@/types/plans-of-study';
import type { Breadcrumb } from '@ps-refarch-ux/neon';
import { getHomeUrl, getStaffRoutePath } from '@/utils/cp-context';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import { CREDIT_GRADES } from '@/constants/plan-of-study-form-constants';
import type { GradeCreditRow } from '@/types/grade-credit-row';
import { htmlToPlainText } from '@/utils/sanitize-rich-html';
import { planHasPlanSpecificCreditRanges } from './credit-ranges-detail-display';

export { CREDIT_GRADES };

/**
 * Checked state for “plan-specific credit alerts” when hydrating create (copy) / edit from API.
 * Reuses the detail predicate so form and detail stay in sync.
 */
export function showPlanSpecificCreditAlertsInForm(plan: {
  showGradeSetting?: boolean;
  gradeSettings?: PlanOfStudyDetail['gradeSettings'];
}): boolean {
  return planHasPlanSpecificCreditRanges(plan);
}

/** Empty grade rows for a new or reset form. */
export function initialGradeSettings(): GradeCreditRow[] {
  return CREDIT_GRADES.map((grade) => ({
    grade,
    minCredits: null,
    maxCredits: null,
  }));
}

/** Grade rows from API detail for edit. */
export function gradeSettingsFromPlan(plan: PlanOfStudyDetail): GradeCreditRow[] {
  return CREDIT_GRADES.map((grade) => {
    const row = plan.gradeSettings?.find((g) => g.grade === grade);
    return {
      grade,
      minCredits: row?.minCredits ?? null,
      maxCredits: row?.maxCredits ?? null,
    };
  });
}

/** Plain text from HTML (for API plain-text fields). */
export function stripHtmlToPlainText(html: string): string {
  return htmlToPlainText(html);
}

/** API payload rows; omits grades with both min and max unset. */
export function gradeSettingsToApiPayload(
  gradeSettings: GradeCreditRow[]
): PlanOfStudyGradeSetting[] {
  return gradeSettings
    .filter(
      (row) =>
        (row.minCredits !== null && row.minCredits !== undefined) ||
        (row.maxCredits !== null && row.maxCredits !== undefined)
    )
    .map(
      (row): PlanOfStudyGradeSetting => ({
        grade: row.grade,
        ...(row.minCredits !== null &&
          row.minCredits !== undefined && { minCredits: row.minCredits }),
        ...(row.maxCredits !== null &&
          row.maxCredits !== undefined && { maxCredits: row.maxCredits }),
      })
    );
}

/** Create/edit form fields used to build the save payload. */
export interface PlanFormState {
  name: string;
  description: string;
  classYearFrom: number | null;
  classYearTo: number | null;
  groupRestrictions: string[];
  alternatesRequired: boolean;
  oneToOneAlternative: boolean;
  showGradeSetting: boolean;
  gradeSettings: GradeCreditRow[];
}

/** Baseline for a brand-new plan (create page, no copy source). */
export function initialEmptyPlanFormState(
  classYearFrom: number,
  classYearTo: number
): PlanFormState {
  return {
    name: '',
    description: '',
    classYearFrom,
    classYearTo,
    groupRestrictions: [],
    alternatesRequired: false,
    oneToOneAlternative: false,
    showGradeSetting: false,
    gradeSettings: initialGradeSettings(),
  };
}

/**
 * True when the selected school id set matches the initial set (order-independent).
 * Used for Published / Active schools drawers: disable Save until the user changes selection.
 */
export function schoolSelectionUnchanged(
  selected: ReadonlySet<string>,
  initial: ReadonlySet<string>
): boolean {
  if (selected.size !== initial.size) {
    return false;
  }
  for (const id of selected) {
    if (!initial.has(id)) {
      return false;
    }
  }
  return true;
}

export interface BuildPlanPayloadOptions {
  requirements?: PlanOfStudyRequirement[];
}

/** Insert a requirement before the synthetic alternates row when present (service keeps alternates last). */
export function insertRequirementBeforeAlternates<T extends { alternates?: boolean }>(
  existing: T[],
  newReq: T
): T[] {
  const list = [...existing];
  const altIndex = list.findIndex((r) => r.alternates);
  if (altIndex >= 0) {
    list.splice(altIndex, 0, newReq);
    return list;
  }
  return [...list, newReq];
}

/** Default name for the synthetic alternates requirement (CPUI parity). */
export const DEFAULT_ALTERNATES_REQUIREMENT_NAME = 'Alternates';

/**
 * Minimal alternates row for create/edit plan saves when General or 1:1 alternates are enabled.
 * Matches CPUI `IRequirement` with `alternates: true`.
 */
export function createDefaultAlternatesRequirement(): PlanOfStudyRequirement {
  return {
    alternates: true,
    name: DEFAULT_ALTERNATES_REQUIREMENT_NAME,
    credits: 0,
    description: '',
    descriptionPlainText: '',
  };
}

export interface MergeRequirementsForAlternatesOptions {
  /**
   * True when the plan form selects General alternates or 1:1 alternates (`alternatesRequired` is true in editor).
   */
  alternatesSelectionEnabled: boolean;
  /**
   * When the user chose “No alternates” in this session and then re-enabled alternates, drop the previous
   * alternates requirement and use a fresh row (no restored course lists).
   */
  forceFreshAlternatesRequirement?: boolean;
}

/**
 * Sync `requirements` with plan-level alternates flags before normalize/save (CPUI parity).
 * Strips the alternates row when alternates are off; appends or keeps one alternates row last when on.
 */
export function mergeRequirementsForAlternatesPlanFlags(
  requirements: PlanOfStudyRequirement[],
  options: MergeRequirementsForAlternatesOptions
): PlanOfStudyRequirement[] {
  const existingAlt = requirements.find((r) => r.alternates);
  const nonAlt = requirements.filter((r) => !r.alternates);

  if (!options.alternatesSelectionEnabled) {
    return nonAlt;
  }

  const forceFresh = Boolean(options.forceFreshAlternatesRequirement);
  if (forceFresh || !existingAlt) {
    return [...nonAlt, createDefaultAlternatesRequirement()];
  }

  return [...nonAlt, existingAlt];
}

type RequirementCoursesDehydrate = {
  mandatedCourses?: Array<{
    course?: { id?: string; name?: string; status?: string; subjectAreaName?: string };
    grade?: number;
  }>;
  groupCourses?: Array<{ name?: string; courses?: Array<{ id?: string }> }>;
  choicesByGradeCourses?: Array<{
    grade?: number;
    courses?: Array<Array<{ id?: string }>>;
  }>;
  itemsFromAListCourses?: Array<Record<string, unknown>>;
};

function dehydrateRequirementCoursesCopy(rc: unknown): unknown {
  if (rc === null || rc === undefined || typeof rc !== 'object') {
    return rc;
  }
  const clone = JSON.parse(JSON.stringify(rc)) as RequirementCoursesDehydrate;

  if (clone.mandatedCourses?.length) {
    for (const mc of clone.mandatedCourses) {
      if (
        mc.course &&
        typeof mc.course === 'object' &&
        mc.course.id !== undefined &&
        mc.course.id !== null
      ) {
        mc.course = {
          id: mc.course.id,
          ...(mc.course.name !== undefined ? { name: mc.course.name } : {}),
          ...(mc.course.status !== undefined ? { status: mc.course.status } : {}),
          ...(mc.course.subjectAreaName !== undefined
            ? { subjectAreaName: mc.course.subjectAreaName }
            : {}),
        };
      }
    }
  }
  if (clone.groupCourses?.length) {
    for (const g of clone.groupCourses) {
      if (g.courses?.length) {
        g.courses = g.courses.map((c) => ({ id: c.id }));
      }
    }
  }
  if (clone.choicesByGradeCourses?.length) {
    for (const ch of clone.choicesByGradeCourses) {
      if (ch.courses?.length) {
        ch.courses = ch.courses.map((courseList) => courseList.map((c) => ({ id: c.id })));
      }
    }
  }
  if (clone.itemsFromAListCourses?.length) {
    for (const item of clone.itemsFromAListCourses) {
      for (const key of Object.keys(item)) {
        if (!key.includes('list')) {
          continue;
        }
        const listRule = item[key] as { courses?: Array<{ id?: string }> } | undefined;
        if (listRule?.courses?.length) {
          listRule.courses = listRule.courses.map((c) => ({ id: c.id }));
        }
      }
    }
  }
  return clone;
}

function isRequirementCoursesEmpty(rc: unknown): boolean {
  if (rc === null || rc === undefined || typeof rc !== 'object') {
    return true;
  }
  const o = rc as Record<string, unknown>;
  const keys = [
    'mandatedCourses',
    'groupCourses',
    'choicesByGradeCourses',
    'itemsFromAListCourses',
  ] as const;
  return keys.every((k) => !Array.isArray(o[k]) || (o[k] as unknown[]).length === 0);
}

/**
 * Count of course rows linked in `requirementCourses` (plan requirements table “Course count”).
 */
export function countLinkedCoursesInRequirement(requirementCourses: unknown): number {
  if (
    requirementCourses === null ||
    requirementCourses === undefined ||
    typeof requirementCourses !== 'object'
  ) {
    return 0;
  }
  const o = requirementCourses as Record<string, unknown>;
  let n = 0;

  const mandated = o.mandatedCourses;
  if (Array.isArray(mandated)) {
    for (const m of mandated) {
      const course = m && typeof m === 'object' ? (m as { course?: unknown }).course : undefined;
      if (course !== undefined && course !== null) {
        n += 1;
      }
    }
  }

  const groups = o.groupCourses;
  if (Array.isArray(groups)) {
    for (const g of groups) {
      if (!g || typeof g !== 'object') {
        continue;
      }
      const courses = (g as { courses?: unknown[] }).courses;
      if (Array.isArray(courses)) {
        n += courses.filter((c) => c !== null && c !== undefined && typeof c === 'object').length;
      }
    }
  }

  const choices = o.choicesByGradeCourses;
  if (Array.isArray(choices)) {
    for (const ch of choices) {
      if (!ch || typeof ch !== 'object') {
        continue;
      }
      const courses = (ch as { courses?: unknown[][] }).courses;
      if (!Array.isArray(courses)) {
        continue;
      }
      for (const list of courses) {
        if (Array.isArray(list)) {
          n += list.filter((c) => c !== null && c !== undefined && typeof c === 'object').length;
        }
      }
    }
  }

  const items = o.itemsFromAListCourses;
  if (Array.isArray(items)) {
    for (const item of items) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const rec = item as Record<string, unknown>;
      for (const key of Object.keys(rec)) {
        if (!key.toLowerCase().includes('list')) {
          continue;
        }
        const listRule = rec[key];
        if (!listRule || typeof listRule !== 'object') {
          continue;
        }
        const courses = (listRule as { courses?: unknown[] }).courses;
        if (Array.isArray(courses)) {
          n += courses.filter((c) => c !== null && c !== undefined && typeof c === 'object').length;
        }
      }
    }
  }

  return n;
}

/**
 * True when Details-tab fields differ from the loaded requirement (edit mode).
 * Used so Save stays disabled until the user changes something (unlike create requirement).
 */
export function isRequirementDetailDirty(
  requirement: PlanOfStudyRequirement,
  name: string,
  credits: number | undefined,
  description: string,
  substitutionAllowed: boolean
): boolean {
  const nameEq = name.trim() === (requirement.name ?? '').trim();
  const descEq = description.trim() === (requirement.description ?? '').trim();
  const reqC = requirement.credits;
  let creditsEq = false;
  if (credits === undefined) {
    creditsEq = reqC === undefined || reqC === null;
  } else if (!Number.isFinite(credits)) {
    creditsEq = false;
  } else if (reqC === undefined || reqC === null) {
    creditsEq = credits === 0;
  } else {
    creditsEq = credits === Number(reqC);
  }

  if (requirement.alternates) {
    return !(nameEq && descEq && creditsEq);
  }

  const subEq = substitutionAllowed === Boolean(requirement.isElectiveRequirement);
  return !(nameEq && descEq && subEq && creditsEq);
}

/** Backend expects `description` / `descriptionPlainText` as `''` when there is no user text (not omitted / undefined). */
function normalizeRequirementDescriptionFieldsForSave(target: PlanOfStudyRequirement): void {
  const htmlRaw = typeof target.description === 'string' ? target.description : '';
  const trimmedHtml = htmlRaw.trim();
  const plainExplicit =
    typeof target.descriptionPlainText === 'string' ? target.descriptionPlainText.trim() : '';
  const plainFromHtml = stripHtmlToPlainText(trimmedHtml);
  const plain = plainExplicit || plainFromHtml;

  if (!plain) {
    target.description = '';
    target.descriptionPlainText = '';
  } else {
    target.description = trimmedHtml;
    target.descriptionPlainText = plainExplicit || plainFromHtml;
  }
}

/**
 * Align requirement objects with CPUI saves: drop empty shells, strip decorative fields,
 * dehydrate course summaries to `{ id }` only (matches `requirementCourseDehydrator`).
 */
export function normalizeRequirementsForPlanSave(
  requirements: PlanOfStudyRequirement[]
): PlanOfStudyRequirement[] {
  return requirements.map((req) => {
    if (req.alternates) {
      const { icon, ...rest } = req;
      return icon && icon.trim() !== '' ? { ...rest, icon } : { ...rest };
    }
    const { icon, requirementCourses, alternates: _a, ...rest } = req;
    const next: PlanOfStudyRequirement = { ...rest };
    if (icon && icon.trim() !== '') {
      next.icon = icon;
    }
    if (
      requirementCourses !== undefined &&
      requirementCourses !== null &&
      !isRequirementCoursesEmpty(requirementCourses)
    ) {
      next.requirementCourses = dehydrateRequirementCoursesCopy(requirementCourses);
    }
    normalizeRequirementDescriptionFieldsForSave(next);
    return next;
  });
}

/**
 * Merge school lists and persistence fields from GET detail into a save payload (CPUI sends the full plan on PUT).
 */
export function withPlanSchoolMetadataForUpdate(
  plan: PlanOfStudyDetail,
  payload: CreatePlanOfStudyPayload
): CreatePlanOfStudyPayload & {
  publishedSchools: Array<{ nid: string }>;
  activeSchools: Array<{ nid: string }>;
  guid?: string;
  type?: number;
  fileVersionId?: string;
  publishedVersion?: boolean;
  lastPublished?: number | null;
  lastSaved?: number | null;
  checkedToPublish?: boolean;
  checkedSchools?: Array<{ nid: string }>;
} {
  const merged: CreatePlanOfStudyPayload & {
    publishedSchools: Array<{ nid: string }>;
    activeSchools: Array<{ nid: string }>;
    guid?: string;
    type?: number;
    fileVersionId?: string;
    publishedVersion?: boolean;
    lastPublished?: number | null;
    lastSaved?: number | null;
    checkedToPublish?: boolean;
    checkedSchools?: Array<{ nid: string }>;
  } = {
    ...payload,
    publishedSchools: plan.publishedSchools ?? [],
    activeSchools: plan.activeSchools ?? [],
  };
  if (plan.guid) {
    merged.guid = plan.guid;
  }
  if (plan.type !== undefined) {
    merged.type = plan.type;
  }
  if (plan.fileVersionId) {
    merged.fileVersionId = plan.fileVersionId;
  }
  if (plan.publishedVersion !== undefined) {
    merged.publishedVersion = plan.publishedVersion;
  }
  if ('lastPublishedMs' in plan) {
    merged.lastPublished = plan.lastPublishedMs ?? null;
  }
  if ('lastSavedMs' in plan) {
    merged.lastSaved = plan.lastSavedMs ?? null;
  }
  if (plan.checkedToPublish !== undefined) {
    merged.checkedToPublish = plan.checkedToPublish;
  }
  if (plan.checkedSchools !== undefined) {
    merged.checkedSchools = plan.checkedSchools;
  }
  return merged;
}

/** Save payload from form state (`requirements` from options). */
export function buildPlanPayloadFromFormState(
  state: PlanFormState,
  options?: BuildPlanPayloadOptions
): CreatePlanOfStudyPayload {
  if (state.classYearFrom === null || state.classYearTo === null) {
    throw new Error('classYearFrom and classYearTo must be set before building the plan payload');
  }
  const trimmedDesc = state.description.trim();
  const gradeSettingsPayload = state.showGradeSetting
    ? gradeSettingsToApiPayload(state.gradeSettings)
    : [];
  const payload: CreatePlanOfStudyPayload = {
    name: state.name.trim(),
    description: trimmedDesc || undefined,
    descriptionPlainText: trimmedDesc ? stripHtmlToPlainText(trimmedDesc) : undefined,
    classYearFrom: state.classYearFrom,
    classYearTo: state.classYearTo,
    groupRestrictions: state.groupRestrictions.length > 0 ? state.groupRestrictions : undefined,
    alternatesRequired: state.alternatesRequired,
    oneToOneAlternative: state.oneToOneAlternative,
    showGradeSetting: state.showGradeSetting,
    gradeSettings: gradeSettingsPayload,
    gradeSettingsField: JSON.stringify(gradeSettingsPayload),
    requirements: options?.requirements ?? [],
  };
  return payload;
}

/**
 * When `planFormStateFromDetail` yields empty HTML, `buildPlanPayloadFromFormState` omits `description` /
 * `descriptionPlainText`. If GET detail still has plain text (or non-empty HTML), restore those fields so
 * requirement-only PUTs do not clear plan descriptions on the server.
 */
export function applyPlanDescriptionFromDetailToPayload(
  payload: CreatePlanOfStudyPayload,
  plan: PlanOfStudyDetail
): CreatePlanOfStudyPayload {
  if (payload.description !== undefined || payload.descriptionPlainText !== undefined) {
    return payload;
  }
  const htmlTrim = (plan.description ?? '').trim();
  const plainTrim = (plan.descriptionPlainText ?? '').trim();
  if (htmlTrim === '' && plainTrim === '') {
    return payload;
  }
  return {
    ...payload,
    description: plan.description ?? '',
    descriptionPlainText:
      plainTrim !== ''
        ? (plan.descriptionPlainText ?? '')
        : stripHtmlToPlainText(plan.description ?? ''),
  };
}

function sortedGroupRestrictionIds(groups: string[]): string[] {
  return [...groups].sort();
}

function gradeCreditRowsEqual(a: GradeCreditRow[], b: GradeCreditRow[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (row, i) =>
      row.grade === b[i].grade &&
      row.minCredits === b[i].minCredits &&
      row.maxCredits === b[i].maxCredits
  );
}

/** Compare form fields except plan name (used after copy: server already stored the new name). */
export function planFormStatesEqualIgnoringName(a: PlanFormState, b: PlanFormState): boolean {
  if (a.description.trim() !== b.description.trim()) {
    return false;
  }
  if (a.classYearFrom !== b.classYearFrom) {
    return false;
  }
  if (a.classYearTo !== b.classYearTo) {
    return false;
  }
  const ga = sortedGroupRestrictionIds(a.groupRestrictions);
  const gb = sortedGroupRestrictionIds(b.groupRestrictions);
  if (ga.length !== gb.length || !ga.every((id, i) => id === gb[i])) {
    return false;
  }
  if (a.alternatesRequired !== b.alternatesRequired) {
    return false;
  }
  if (a.oneToOneAlternative !== b.oneToOneAlternative) {
    return false;
  }
  if (a.showGradeSetting !== b.showGradeSetting) {
    return false;
  }
  return gradeCreditRowsEqual(a.gradeSettings, b.gradeSettings);
}

/** Form snapshot from GET detail (for comparing copy-mode form to source plan). */
export function planFormStateFromDetail(plan: PlanOfStudyDetail): PlanFormState {
  return {
    name: plan.name,
    description: plan.description ?? '',
    classYearFrom: plan.classYearFrom,
    classYearTo: plan.classYearTo,
    groupRestrictions: [...(plan.groupRestrictions ?? [])],
    alternatesRequired: plan.alternatesRequired ?? false,
    oneToOneAlternative: plan.oneToOneAlternative ?? false,
    showGradeSetting: plan.showGradeSetting ?? false,
    gradeSettings: gradeSettingsFromPlan(plan),
  };
}

/** Whether two plan form snapshots are equivalent (for dirty / save enablement). */
export function planFormStatesEqual(a: PlanFormState, b: PlanFormState): boolean {
  if (a.name.trim() !== b.name.trim()) {
    return false;
  }
  return planFormStatesEqualIgnoringName(a, b);
}

/** Alternates summary line for detail view. */
export function getAlternatesDisplayText(
  alternatesRequired: boolean,
  oneToOneAlternative: boolean
): string {
  if (oneToOneAlternative) {
    return 'Require 1-to-1 alternates';
  }
  if (alternatesRequired) {
    return 'Allow general alternates';
  }
  return 'No alternates required';
}

/** Home → Plans of study → `lastSegment`. */
export function getPlansOfStudyBreadcrumbs(
  lastSegment: string,
  lastUrl: string = '#'
): Breadcrumb[] {
  return [
    { text: 'Home', url: getHomeUrl() },
    { text: 'Plans of study', url: getStaffRoutePath(STAFF_ROUTES.plansOfStudy.list) },
    { text: lastSegment, url: lastUrl },
  ];
}
